'use client';

/* eslint-disable @next/next/no-img-element */

import React from 'react';
import Markdown from 'markdown-to-jsx';

import ArticleVideo from './ArticleVideo';
import LinkDownload from './LinkDownload';
import FAQClient from './FAQClient';
import CodeBlock from './CodeBlock';

const Heading1 = ({ children }) => {
  const id = `title-1-${children.toString().toLowerCase().replace(/\s+/g, '-').replace(/[^\w-]/g, '')}`;
  return (
    <h1
      id={id}
      className="card-title mb-4 pb-3 border-bottom border-3 border-primary fw-bold"
    >
      {children}
    </h1>
  );
};

const Heading2 = ({ children }) => {
  const id = `title-2-${children.toString().toLowerCase().replace(/\s+/g, '-').replace(/[^\w-]/g, '')}`;
  return (
    <h2
      id={id}
      className="card-title toc mb-3 mt-5 fw-semibold text-dark text-decoration-underline"
    >
      {children}
    </h2>
  );
};

const Heading3 = ({ children }) => {
  const id = `title-3-${children.toString().toLowerCase().replace(/\s+/g, '-').replace(/[^\w-]/g, '')}`;
  return (
    <h3
      id={id}
      className="card-title toc mb-3 mt-4 fw-semibold text-dark"
    >
      {children}
    </h3>
  );
};

const Heading4 = ({ children }) => {
  const id = `title-4-${children.toString().toLowerCase().replace(/\s+/g, '-').replace(/[^\w-]/g, '')}`;
  return (
    <h4
      id={id}
      className="card-title toc mb-2 mt-3 fw-semibold text-dark"
    >
      {children}
    </h4>
  );
};

const Heading5 = ({ children }) => {
  const id = `title-5-${children.toString().toLowerCase().replace(/\s+/g, '-').replace(/[^\w-]/g, '')}`;
  return (
    <h5
      id={id}
      className="card-title toc mb-2 mt-3 fw-semibold text-dark"
    >
      {children}
    </h5>
  );
};

const Paragraph = ({ children }) => <p className="text-dark mt-3" align="justify">{children}</p>;

const Dl = ({ children }) => <dl className="row text-dark mt-3">{children}</dl>;
const Dd = ({ children }) => <dd className="col-sm-9">{children}</dd>;
const Dt = ({ children }) => <dt className="col-sm-3">{children}</dt>;

const Accordion = () => (
  <div className="accordion" id="accordionExample">
    <div className="accordion-item">
      <h2 className="accordion-header" id="headingOne">
        <button className="accordion-button" type="button" data-bs-toggle="collapse" data-bs-target="#collapseOne" aria-expanded="true" aria-controls="collapseOne">
          Accordion Item #1
        </button>
      </h2>
      <div id="collapseOne" className="accordion-collapse collapse show" aria-labelledby="headingOne" data-bs-parent="#accordionExample">
        <div className="accordion-body">
          <strong>This is the first items accordion body.</strong>
          {' '}
          It is shown by default, until the collapse plugin adds the appropriate classes that we
          {' '}
          <code>.accordion-body</code>
          , though the transition does limit overflow.
        </div>
      </div>
    </div>
  </div>
);

const Image = ({ src, alt }) => (
  <img
    className="img-fluid rounded mx-auto d-block"
    src={src}
    alt={alt}
  />
);

const Ol = ({ children }) => (
  <ol className="text-dark mt-3">{children}</ol>
);

const Ul = ({ children }) => (
  <ul className="text-dark">
    {children}
  </ul>
);

const BlockQuote = ({ children }) => (
  <blockquote className="blockquote mb-4 mt-4 fst-italic p-4 border-start border-4 border-primary bg-light position-relative">
    <div className="text-dark fs-5">
      { children }
    </div>
    <div className="position-absolute start-0 top-0 ps-2">
      <i className="fas fa-quote-left text-primary opacity-25 fs-3" />
    </div>
  </blockquote>
);

const Table = ({ children }) => (
  <table className="table table-hover">
    {children}
  </table>
);

const FAQ = ({ children }) => {
  // Parse markdown text to extract FAQ items
  const text = React.Children.toArray(children).join('');

  const faqItems = [];

  // Simple split approach - split by lines starting with ####
  const lines = text.split('\n');
  let currentQuestion = null;
  let currentAnswer = [];

  lines.forEach((line) => {
    if (line.trim().startsWith('####')) {
      // Save previous item if exists
      if (currentQuestion) {
        faqItems.push({
          id: `faq-${faqItems.length + 1}`,
          question: currentQuestion,
          answer: currentAnswer.join('\n').trim(),
        });
      }
      // Start new item
      currentQuestion = line.replace(/^####+\s+/, '').trim();
      currentAnswer = [];
    } else if (currentQuestion) {
      // Add to current answer
      currentAnswer.push(line);
    }
  });

  // Add last item
  if (currentQuestion) {
    faqItems.push({
      id: `faq-${faqItems.length + 1}`,
      question: currentQuestion,
      answer: currentAnswer.join('\n').trim(),
    });
  }

  return <FAQClient faqItems={faqItems} />;
};

function ArticleContent({
  markdown, image, title, imageTemplate,
}) {
  return (
    <div className="card blog blog-detail border-0 shadow rounded">
      <img
        src={image || imageTemplate}
        className="img-fluid rounded-top"
        alt={`Article${title}: article.image`}
      />
      <div className="card-body content">
        <Markdown options={{
          overrides: {
            h1: Heading1,
            h2: Heading2,
            h3: Heading3,
            h4: Heading4,
            h5: Heading5,
            p: Paragraph,
            ol: Ol,
            ul: Ul,
            dl: Dl,
            dt: Dt,
            dd: Dd,
            code: CodeBlock,
            img: Image,
            accordion: Accordion,
            table: Table,
            faq: FAQ,
            a: LinkDownload,
            blockquote: BlockQuote,
            video: ArticleVideo,
            br: () => <br />,
          },
        }}
        >
          {markdown}
        </Markdown>
      </div>
    </div>
  );
}

export default ArticleContent;
